import React, { useEffect, useRef, useState } from 'react';
import FlexComponent from '../FlexComponent';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import { Avatar } from 'primereact/avatar';
import { ErrorMessage } from 'formik';

function CosignerAddressHistory({
  values,
  errors,
  index,
  isNewEntry,
  handleNewEntryChange,
  touched,
  handleBlur,
  setFieldValue,
  toast,
  currentCosigner,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ newEntry: {}, addressHistory: [] });
  const inputRef = useRef(null);
  const autoCompleteRef = useRef(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newAddress, setNewAddress] = useState({
    full_address: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    home_status: '',
    status: !(currentCosigner?.address_history?.length || 0), // True for first address
    rent_amount: null,
    move_in_date: null,
    move_out_date: null,
  });

  // Safely access address_history
  const addressHistory = isNewEntry ? currentCosigner?.address_history || [] : values.cosigner_list[index]?.address_history || [];

  useEffect(() => {
    if (window.google && window.google.maps && inputRef.current) {
      if (!inputRef.current || !(inputRef.current instanceof HTMLInputElement)) return;
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode'],
        componentRestrictions: { country: 'us' },
      });

      const listener = autoCompleteRef.current.addListener('place_changed', () => {
        const place = autoCompleteRef.current.getPlace();
        if (!place.geometry) {
          return;
        }

        let streetNumber = '';
        let route = '';
        const updatedAddress = {
          full_address: place.formatted_address || '',
          street_address: '',
          city: '',
          state: '',
          zip_code: '',
          home_status: newAddress.home_status,
          status: newAddress.status,
          rent_amount: newAddress.rent_amount,
          move_in_date: newAddress.move_in_date,
          move_out_date: newAddress.move_out_date,
        };

        (place.address_components || []).forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) streetNumber = component.long_name;
          if (types.includes('route')) route = component.long_name;
          if (types.includes('locality')) updatedAddress.city = component.long_name;
          if (types.includes('administrative_area_level_1')) updatedAddress.state = component.long_name;
          if (types.includes('postal_code')) updatedAddress.zip_code = component.long_name;
        });

        if (streetNumber || route) updatedAddress.street_address = `${streetNumber} ${route}`.trim();

        setNewAddress(updatedAddress);
        // Re-validate the new address
        const validationErrors = validateAddress(updatedAddress, updatedAddress.status);
        setError((prev) => ({ ...prev, newEntry: validationErrors }));
      });

      return () => {
        if (autoCompleteRef.current && listener) {
          window.google.maps.event.removeListener(listener);
        }
      };
    } else {
      console.error('Google Maps API not loaded');
    }
  }, [newAddress.status, newAddress.home_status, newAddress.rent_amount, newAddress.move_in_date, newAddress.move_out_date]);

  const calculateDuration = () => {
    if (!addressHistory.length) return 0;
    let totalMonths = 0;
    addressHistory.forEach((address) => {
      if (address.move_in_date) {
        const start = new Date(address.move_in_date);
        const end = address.move_out_date ? new Date(address.move_out_date) : new Date();
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        totalMonths += months > 0 ? months : 0;
      }
    });
    return totalMonths / 12;
  };

  const validateAddress = (address, isCurrent) => {
    const errors = {};
    if (!address.full_address?.trim()) errors.full_address = true;
    if (!address.home_status) errors.home_status = true;
    if (!address.move_in_date) errors.move_in_date = true;
    if (!isCurrent && !address.move_out_date) errors.move_out_date = true;
    // if (address.home_status === 'Rent' && !address.rent_amount) errors.rent_amount = true;
    return errors;
  };

  const handleAddClick = () => {
    const isCurrent = newAddress.status;
    const validationErrors = validateAddress(newAddress, isCurrent);

    if (Object.keys(validationErrors).length > 0) {
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
      return;
    }

    setLoading(true);
    const updatedHistory = [...addressHistory, { ...newAddress }];
    if (isNewEntry) {
      handleNewEntryChange('address_history', updatedHistory);
    } else {
      setFieldValue(`cosigner_list.${index}.address_history`, updatedHistory);
    }

    setNewAddress({
      full_address: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      home_status: '',
      status: false,
      rent_amount: null,
      move_in_date: null,
      move_out_date: null,
    });
    setError((prev) => ({ ...prev, newEntry: {} }));
    setLoading(false);
  };

  const handleEditClick = (idx) => {
    setEditingIndex(idx);
    // Initialize errors for this index if not already present
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[idx] = newAddressHistoryErrors[idx] || {};
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleSaveEdit = (idx) => {
    const address = addressHistory[idx];
    const isCurrent = address.status || idx === 0;
    const validationErrors = validateAddress(address, isCurrent);

    if (Object.keys(validationErrors).length > 0) {
      setError((prev) => {
        const newAddressHistoryErrors = [...prev.addressHistory];
        newAddressHistoryErrors[idx] = validationErrors;
        return { ...prev, addressHistory: newAddressHistoryErrors };
      });
      return;
    }

    setEditingIndex(null);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[idx] = {};
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[editingIndex] = {};
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleDeleteClick = (idx) => {
    const updatedHistory = addressHistory.filter((_, i) => i !== idx);
    if (isNewEntry) {
      handleNewEntryChange('address_history', updatedHistory);
    } else {
      setFieldValue(`cosigner_list.${index}.address_history`, updatedHistory);
    }
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors.splice(idx, 1); // Remove errors for deleted entry
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleInputChange = (idx, field, value) => {
    const updatedHistory = [...addressHistory];
    updatedHistory[idx] = { ...updatedHistory[idx], [field]: value };
    if (isNewEntry) {
      handleNewEntryChange('address_history', updatedHistory);
    } else {
      setFieldValue(`cosigner_list.${index}.address_history`, updatedHistory);
    }
    // Re-validate the entry
    const validationErrors = validateAddress(updatedHistory[idx], updatedHistory[idx].status || idx === 0);
    setError((prev) => {
      const newAddressHistoryErrors = [...prev.addressHistory];
      newAddressHistoryErrors[idx] = validationErrors;
      return { ...prev, addressHistory: newAddressHistoryErrors };
    });
  };

  const handleNewEntryChangeLocal = (field, value) => {
    setNewAddress((prev) => {
      const newState = { ...prev, [field]: value };
      // Re-validate the new address
      const validationErrors = validateAddress(newState, newState.status);
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
      return newState;
    });
  };

  const changeAvatarColor = (homeStatus) => {
    if (!homeStatus) return '#8C8C8C';
    if (homeStatus === 'Rent') return '#F141D0';
    if (homeStatus === 'Own') return '#418BCC';
    if (homeStatus === 'Live Rent Free') return '#EB4243';
    return '#8C8C8C';
  };

  const avatarLabel = (homeStatus) => {
    if (!homeStatus) return '?';
    if (homeStatus === 'Rent') return 'R';
    if (homeStatus === 'Own') return 'O';
    if (homeStatus === 'Live Rent Free') return 'RF';
    return '?';
  };

  const isLessThanTwoYears = calculateDuration() < 2;

  return (
    <div style={{ width: '100%', boxShadow: 'rgba(0, 0, 0, 0.2) 0px 4px 10px 4px' }} id="addressId">
      <div>
        <FlexComponent>
          <div>
            <p style={{ fontSize: '20px', fontWeight: 'bold', padding: '0.75rem' }}>
              {addressHistory.length > 0 ? 'Where did you live before?' : 'Where do you live now?'}
            </p>
            <p style={{ padding: '0.75rem' }}>Address History</p>
          </div>
        </FlexComponent>
        {isLessThanTwoYears && (
          <p style={{ color: 'blue', fontSize: '14px', padding: '.2rem 0.75rem' }}>
            Please provide at least 2 years of address history.
          </p>
        )}
      </div>

      {addressHistory.length > 0 && (
        <div>
          {addressHistory.map((address, idx) => (
            <div key={idx} style={{ background: 'white', padding: '0 1rem', borderRadius: '10px', margin: '1rem 0' }}>
              {editingIndex !== idx ? (
                <FlexComponent alignItems="start" justifyContent="start" gap="1rem" style={{ textAlign: 'start' }}>
                  <div>
                    <Avatar
                      style={{ width: '4.5rem', color: 'white', background: changeAvatarColor(address?.home_status) }}
                      label={avatarLabel(address?.home_status)}
                      size="xlarge"
                    />
                  </div>
                  <div style={{ width: '50%' }}>
                    <p>{address?.full_address || 'No Address'}</p>
                    <p>
                      {address?.city && address?.state && address?.zip_code
                        ? `${address.city}, ${address.state} ${address.zip_code}`
                        : 'No City/State/ZIP'}
                    </p>
                    <p>{address?.home_status || 'No Home Status'}</p>
                    {address?.rent_amount && <p>Rent Amount: ${address.rent_amount}</p>}
                    <p>Move In: {address?.move_in_date ? new Date(address.move_in_date).toLocaleDateString() : 'N/A'}</p>
                    {!address.status && address?.move_out_date && (
                      <p>Move Out: {new Date(address.move_out_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <Button type="button" onClick={() => handleEditClick(idx)}>
                    Edit
                  </Button>
                  {idx !== 0 && (
                    <Button type="button" onClick={() => handleDeleteClick(idx)}>
                      Delete
                    </Button>
                  )}
                </FlexComponent>
              ) : (
                <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
                  <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start">
                    <div
                      className="inputContainerDiv"
                      style={{ width: '40%', border: error.addressHistory[idx]?.full_address && '1px solid red', borderRadius: '5px' }}
                    >
                      <InputText
                        style={{ width: '100%' }}
                        value={address.full_address || ''}
                        onChange={(e) => handleInputChange(idx, 'full_address', e.target.value)}
                        onBlur={handleBlur}
                        placeholder={idx === 0 ? 'Current Address' : `Previous Address ${idx}`}
                      />
                    </div>
                    <div
                      className="inputContainerDiv"
                      style={{ width: '20%', border: error.addressHistory[idx]?.home_status && '1px solid red', borderRadius: '5px' }}
                    >
                      <Dropdown
                        value={address.home_status || ''}
                        options={[
                          { label: 'Rent', value: 'Rent' },
                          { label: 'Own', value: 'Own' },
                          { label: 'Live Rent Free', value: 'Live Rent Free' },
                        ]}
                        style={{ width: '100%' }}
                        onChange={(e) => handleInputChange(idx, 'home_status', e.value)}
                        onBlur={handleBlur}
                        placeholder="Address Type"
                      />
                    </div>
                    {address.home_status === 'Rent' && (
                      <div
                        className="inputContainerDiv"
                        style={{ width: '20%', border: error.addressHistory[idx]?.rent_amount && '1px solid red', borderRadius: '5px' }}
                      >
                        <InputText
                          type="number"
                          style={{ width: '100%' }}
                          value={address.rent_amount || ''}
                          onChange={(e) => handleInputChange(idx, 'rent_amount', e.target.value)}
                          onBlur={handleBlur}
                          placeholder="Rent Amount"
                        />
                      </div>
                    )}
                    <div
                      className="inputContainerDiv"
                      style={{ width: '20%', border: error.addressHistory[idx]?.move_in_date && '1px solid red', borderRadius: '5px' }}
                    >
                      <div className="p-inputgroup flex-1 login-input-div">
                        <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                          <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                        </span>
                        <Calendar
                          value={address.move_in_date ? new Date(address.move_in_date) : null}
                          onChange={(e) => handleInputChange(idx, 'move_in_date', e.value)}
                          style={{ width: '100%' }}
                          placeholder="Move In Date"
                          maxDate={new Date()}
                          className="inputWithIcon"
                        />
                      </div>
                    </div>
                    {!address.status && (
                      <div
                        className="inputContainerDiv"
                        style={{ width: '20%', border: error.addressHistory[idx]?.move_out_date && '1px solid red', borderRadius: '5px' }}
                      >
                        <div className="p-inputgroup flex-1 login-input-div">
                          <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                            <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                          </span>
                          <Calendar
                            value={address.move_out_date ? new Date(address.move_out_date) : null}
                            onChange={(e) => handleInputChange(idx, 'move_out_date', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Move Out Date"
                            maxDate={new Date()}
                            className="inputWithIcon"
                          />
                        </div>
                      </div>
                    )}
                  </FlexComponent>
                  <FlexComponent justifyContent="center" gap="1rem">
                    <Button type="button" onClick={() => handleSaveEdit(idx)}>
                      Save
                    </Button>
                    <Button type="button" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </FlexComponent>
                </FlexComponent>
              )}
              <Divider />
            </div>
          ))}
        </div>
      )}

      <div style={{ background: 'white', padding: '1rem', borderRadius: '10px', margin: '1rem 0' }}>
        <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
          <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start">
            <div
              className="inputContainerDiv"
              style={{ width: '40%', border: error.newEntry.full_address && '1px solid red', borderRadius: '5px' }}
            >
              <InputText
                ref={inputRef}
                style={{ width: '100%' }}
                value={newAddress.full_address || ''}
                onChange={(e) => handleNewEntryChangeLocal('full_address', e.target.value)}
                onBlur={handleBlur}
                placeholder={addressHistory.length > 0 ? 'Previous Address' : 'Current Address'}
              />
            </div>
            <div
              className="inputContainerDiv"
              style={{ width: '20%', border: error.newEntry.home_status && '1px solid red', borderRadius: '5px' }}
            >
              <Dropdown
                value={newAddress.home_status || ''}
                options={[
                  { label: 'Rent', value: 'Rent' },
                  { label: 'Own', value: 'Own' },
                  { label: 'Live Rent Free', value: 'Live Rent Free' },
                ]}
                style={{ width: '100%' }}
                onChange={(e) => handleNewEntryChangeLocal('home_status', e.value)}
                onBlur={handleBlur}
                placeholder="Address Type"
              />
            </div>
            {newAddress.home_status === 'Rent' && (
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.rent_amount && '1px solid red', borderRadius: '5px' }}
              >
                <InputText
                  type="number"
                  style={{ width: '100%' }}
                  value={newAddress.rent_amount || ''}
                  onChange={(e) => handleNewEntryChangeLocal('rent_amount', e.target.value)}
                  onBlur={handleBlur}
                  placeholder="Rent Amount"
                />
              </div>
            )}
            <div
              className="inputContainerDiv"
              style={{ width: '20%', border: error.newEntry.move_in_date && '1px solid red', borderRadius: '5px' }}
            >
              <div className="p-inputgroup flex-1 login-input-div">
                <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                  <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                </span>
                <Calendar
                  value={newAddress.move_in_date ? new Date(newAddress.move_in_date) : null}
                  onChange={(e) => handleNewEntryChangeLocal('move_in_date', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Move In Date"
                  maxDate={new Date()}
                  className="inputWithIcon"
                />
              </div>
            </div>
            {!newAddress.status && (
              <div
                className="inputContainerDiv"
                style={{ width: '20%', border: error.newEntry.move_out_date && '1px solid red', borderRadius: '5px' }}
              >
                <div className="p-inputgroup flex-1 login-input-div">
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                  </span>
                  <Calendar
                    value={newAddress.move_out_date ? new Date(newAddress.move_out_date) : null}
                    onChange={(e) => handleNewEntryChangeLocal('move_out_date', e.value)}
                    style={{ width: '100%' }}
                    placeholder="Move Out Date"
                    maxDate={new Date()}
                    className="inputWithIcon"
                  />
                </div>
              </div>
            )}
          </FlexComponent>
          <FlexComponent justifyContent="center" gap="1rem">
            <Button
              type="button"
              label={newAddress.status ? 'Save Current Address' : 'Save Previous Address'}
              style={{ borderRadius: '5px', fontSize: '16px' }}
              onClick={handleAddClick}
              disabled={loading}
            />
            <Button
              type="button"
              label="Cancel"
              style={{ borderRadius: '5px', fontSize: '16px', padding: '5px 20px', border: '1px solid #E0E4EC', background: 'white', color: '#8C8C8C' }}
              onClick={() => {
                setNewAddress({
                  full_address: '',
                  street_address: '',
                  city: '',
                  state: '',
                  zip_code: '',
                  home_status: '',
                  status: addressHistory.length === 0,
                  rent_amount: null,
                  move_in_date: null,
                  move_out_date: null,
                });
                setError((prev) => ({ ...prev, newEntry: {} }));
              }}
            />
          </FlexComponent>
        </FlexComponent>
        <div style={{ textAlign: 'center', marginTop: '1rem', width: '100%' }}>
          <ErrorMessage
            name={`cosigner_list.${index}.address_history`}
            component="div"
            className="error"
            style={{ color: 'red' }}
          />
        </div>
      </div>
    </div>
  );
}

export default CosignerAddressHistory;